/**
 * Adding to the cart without leaving the product page (connector 0.11,
 * `?wc-ajax=personalizy_add`; docs/product-page-plan-20261031.md §6).
 *
 * The widget still commits the design first and writes the usual hidden
 * inputs; instead of submitting the page it posts the form's own fields here,
 * so the connector runs every add-to-cart hook exactly as for the form. A
 * batch id rides along (and stays in the form as `pz_batch`): a repeated
 * request, or the form posted as a fallback after a lost answer, never adds
 * the same thing twice.
 *
 * Outcomes:
 *  - `added`: the connector's lines, cart count, fragments;
 *  - `refused`: the shop said no (a stock limit, a check) — its words;
 *  - `unreachable`: the shop answered, but not with an add (a status check
 *    found nothing, an error page) — the caller may fall back to the classic
 *    form, which the batch id keeps safe;
 *  - `offline`: nothing answered at all, not even a status check — no form
 *    can get through either; the caller keeps the design and the batch id for
 *    a retry.
 */

import { createLayer, type LayerHandle } from './layers.js';

export interface CartAddConfig {
  /** `/?wc-ajax=personalizy_add` */
  url: string;
  /** After adding: stay on the page with the confirmation, or go to the cart. */
  after: 'stay' | 'cart';
  cartUrl: string;
  checkoutUrl?: string;
}

export interface CartAddLine {
  key: string;
  name: string;
  quantity: number;
  total: number;
  /** The pieces deal this line is in (connector 0.12). */
  deal?: { count: number; percent: number; next: { min: number; percent: number } | null; coupon?: boolean };
}

export interface CartAddSuccess {
  ok: true;
  repeat: boolean;
  keys: string[];
  lines: CartAddLine[];
  replaced: boolean;
  count: number;
  subtotal: number;
  fragments: Record<string, string>;
  cartHash: string;
  cartUrl: string;
}

export type CartAddOutcome =
  | { kind: 'added'; body: CartAddSuccess }
  | { kind: 'refused'; messages: string[] }
  | { kind: 'unreachable' }
  | { kind: 'offline' };

export const CART_ADD_TEXTS = {
  added: 'Dodato u korpu',
  addedMany: (n: number): string => `Dodato u korpu (${n})`,
  checkout: 'Završi porudžbinu',
  again: 'Personalizuj još jedan',
  keepShopping: 'Nastavi kupovinu',
  inCart: (count: number): string => `U korpi: ${count} ${count % 10 === 1 && count % 100 !== 11 ? 'proizvod' : 'proizvoda'}`,
  refused: 'Proizvod nije dodat u korpu.',
  dealOn: (n: number, percent: string): string => `U korpi ${n} kom. ovog modela: −${percent} na svaki.`,
  dealNext: (more: number, percent: string): string => `Dodaj još ${more} kom. ovog modela i popust je −${percent}.`,
  dealCoupon: 'Popust za više komada se ne sabira sa kuponom: važi kupon.',
  close: 'Zatvori',
};

/** What a fetch may take before the status of the batch is asked instead. */
export const CART_ADD_TIMEOUT_MS = 30_000;

/**
 * After a request that was cut off the add may still be running on a slow
 * server, and its batch is only recorded when it ends: asked again for a
 * while before the classic form may go.
 */
const AFTER_TIMEOUT_WAITS_MS = [1500, 3000, 5000, 8000];

/** The shop answered, only not usefully (a 5xx, a page that is not JSON). */
class Answered extends Error {}

/** A fresh batch id (RFC 4122 v4). */
export function newBatchId(random: () => number = Math.random): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const hex = (n: number) => Array.from({ length: n }, () => Math.floor(random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${'89ab'[Math.floor(random() * 4)]}${hex(3)}-${hex(12)}`;
}

/**
 * The form's fields as name/value pairs, as a POST of the form would send them
 * (files left out — none of ours, and nothing a cart line can take).
 */
export function formPairs(form: HTMLFormElement): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const data = new FormData(form);
  data.forEach((value, name) => {
    if (typeof value === 'string') out.push([name, value]);
  });
  return out;
}

/** Puts (or updates) a hidden input on the form. */
export function setHidden(form: HTMLFormElement, name: string, value: string): void {
  let input = form.querySelector<HTMLInputElement>(`input[type="hidden"][name="${name}"]`);
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }
  input.value = value;
}

type Fetch = (input: string, init: RequestInit) => Promise<Response>;

async function post(fetchImpl: Fetch, url: string, body: URLSearchParams, timeoutMs: number): Promise<unknown> {
  const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
  try {
    const response = await fetchImpl(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: body.toString(),
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (response.status >= 500) throw new Answered(`HTTP ${response.status}`);
    try {
      return await response.json();
    } catch {
      throw new Answered('not JSON');
    }
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function asSuccess(body: unknown): CartAddSuccess | undefined {
  const b = body as Partial<CartAddSuccess> | null;
  return b && b.ok === true && Array.isArray(b.keys) ? (b as CartAddSuccess) : undefined;
}

function messagesOf(body: unknown): string[] {
  const errors = (body as { errors?: Array<{ messages?: unknown }> } | null)?.errors;
  const out: string[] = [];
  for (const e of Array.isArray(errors) ? errors : []) {
    for (const m of Array.isArray(e?.messages) ? e.messages : []) if (typeof m === 'string' && m.trim()) out.push(m.trim());
  }
  return [...new Set(out)];
}

/**
 * Adds one or more items (each a form's fields) as one batch.
 */
export async function addToCart(
  config: Pick<CartAddConfig, 'url'>,
  items: Array<Array<[string, string]>>,
  batchId: string,
  options: { fetch?: Fetch; timeoutMs?: number; statusTries?: number; wait?: (ms: number) => Promise<void> } = {},
): Promise<CartAddOutcome> {
  const fetchImpl: Fetch = options.fetch ?? ((input, init) => fetch(input, init));
  const wait = options.wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const body = new URLSearchParams();
  body.set('pz_batch', batchId);
  body.set('pz_items', JSON.stringify(items.map((fields) => ({ fields }))));
  let answer: unknown;
  try {
    answer = await post(fetchImpl, config.url, body, options.timeoutMs ?? CART_ADD_TIMEOUT_MS);
  } catch (error) {
    // No answer: maybe it never arrived, maybe only the answer was lost. Ask.
    const timedOut = (error as { name?: string } | null)?.name === 'AbortError';
    let answered = error instanceof Answered;
    const tries = options.statusTries ?? 3;
    const waits = timedOut ? AFTER_TIMEOUT_WAITS_MS : Array.from({ length: tries }, (_, i) => 800 * i);
    for (const ms of waits) {
      if (ms > 0) await wait(ms);
      try {
        const status = new URLSearchParams();
        status.set('pz_batch', batchId);
        status.set('pz_status', '1');
        const done = asSuccess(await post(fetchImpl, config.url, status, 10_000));
        answered = true;
        if (done) return { kind: 'added', body: done };
        // Never arrived: the classic form is safe. Cut off: it may still be running.
        if (!timedOut) return { kind: 'unreachable' };
      } catch (statusError) {
        if (statusError instanceof Answered) answered = true;
      }
    }
    return answered ? { kind: 'unreachable' } : { kind: 'offline' };
  }
  const done = asSuccess(answer);
  if (done) return { kind: 'added', body: done };
  const messages = messagesOf(answer);
  // A refusal the connector could not phrase (origin, a malformed request):
  // nothing was added, and the classic form is the safe way on.
  if (messages.length === 0) return { kind: 'unreachable' };
  return { kind: 'refused', messages };
}

/**
 * What WooCommerce's own AJAX add does with an answer: the fragments (header
 * cart, mini cart) replaced, then `added_to_cart` for the theme, the cart
 * fragments script and the analytics plugins.
 */
export function announceAdded(body: CartAddSuccess, button: HTMLElement | null): void {
  const $ = (window as unknown as { jQuery?: JQueryish }).jQuery;
  if (!$) return;
  for (const [selector, html] of Object.entries(body.fragments ?? {})) {
    try {
      $(selector).replaceWith(html);
    } catch {
      /* a theme's odd selector is not worth failing the add over */
    }
  }
  try {
    $(document.body).trigger('added_to_cart', [body.fragments ?? {}, body.cartHash, button ? $(button) : undefined]);
  } catch {
    /* listeners are other plugins' business */
  }
}

interface JQueryish {
  (target: unknown): { replaceWith(html: string): unknown; trigger(name: string, args?: unknown[]): unknown };
}

export interface AddedSheetOptions {
  host: ShadowRoot | HTMLElement;
  body: CartAddSuccess;
  /** A small picture of the design, when one could be drawn. */
  picture?: string;
  format: (amount: number) => string;
  cartUrl: string;
  onAgain: () => void;
  navigate?: (url: string) => void;
}

/**
 * "Dodato u korpu": a bottom sheet on a phone, a small card on a wide screen.
 * The way to the cart is the primary button; personalizing another one and
 * going on shopping are one tap each.
 */
export function showAddedSheet(options: AddedSheetOptions): LayerHandle {
  const { body } = options;
  const navigate = options.navigate ?? ((url: string) => window.location.assign(url));
  const card = document.createElement('div');
  card.className = 'pz-added-card';

  const head = document.createElement('div');
  head.className = 'pz-added-head';
  const tick = document.createElement('span');
  tick.className = 'pz-added-tick';
  tick.setAttribute('aria-hidden', 'true');
  tick.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5 10 17 19 7"/></svg>';
  const title = document.createElement('strong');
  title.className = 'pz-added-title';
  const pieces = body.lines.reduce((sum, l) => sum + (l.quantity || 0), 0);
  title.textContent = pieces > 1 ? CART_ADD_TEXTS.addedMany(pieces) : CART_ADD_TEXTS.added;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'pz-added-close';
  close.setAttribute('aria-label', CART_ADD_TEXTS.close);
  close.innerHTML = '&#10005;';
  head.append(tick, title, close);

  const list = document.createElement('div');
  list.className = 'pz-added-lines';
  body.lines.forEach((line, i) => {
    const row = document.createElement('div');
    row.className = 'pz-added-line';
    if (i === 0 && options.picture) {
      const img = document.createElement('img');
      img.className = 'pz-added-pic';
      img.alt = '';
      img.src = options.picture;
      row.appendChild(img);
    }
    const text = document.createElement('div');
    text.className = 'pz-added-text';
    const name = document.createElement('span');
    name.className = 'pz-added-name';
    name.textContent = line.name;
    const meta = document.createElement('span');
    meta.className = 'pz-added-meta';
    meta.textContent = `${line.quantity} × · ${options.format(line.total)}`;
    text.append(name, meta);
    row.appendChild(text);
    list.appendChild(row);
  });

  // The deal: what the cart reached, and what one more would bring.
  const deal = body.lines.find((l) => l.deal)?.deal;
  let dealNote: HTMLElement | undefined;
  if (deal) {
    const pct = (p: number): string => String(Math.round(p * 100) / 100).replace('.', ',') + '%';
    const note = document.createElement('p');
    note.className = 'pz-added-deal';
    note.textContent = deal.coupon
      ? CART_ADD_TEXTS.dealCoupon
      : [deal.percent > 0 ? CART_ADD_TEXTS.dealOn(deal.count, pct(deal.percent)) : '', deal.next ? CART_ADD_TEXTS.dealNext(deal.next.min - deal.count, pct(deal.next.percent)) : '']
        .filter(Boolean)
        .join(' ');
    if (note.textContent) dealNote = note;
  }

  const summary = document.createElement('p');
  summary.className = 'pz-added-summary';
  summary.textContent = `${CART_ADD_TEXTS.inCart(body.count)} · ${options.format(body.subtotal)}`;

  const actions = document.createElement('div');
  actions.className = 'pz-added-actions';
  const checkout = document.createElement('button');
  checkout.type = 'button';
  checkout.className = 'pz-btn pz-added-checkout';
  checkout.textContent = CART_ADD_TEXTS.checkout;
  const again = document.createElement('button');
  again.type = 'button';
  again.className = 'pz-btn-ghost pz-added-again';
  again.textContent = CART_ADD_TEXTS.again;
  const keep = document.createElement('button');
  keep.type = 'button';
  keep.className = 'pz-added-keep';
  keep.textContent = CART_ADD_TEXTS.keepShopping;
  actions.append(checkout, again, keep);

  card.append(head, list);
  if (dealNote) card.append(dealNote);
  card.append(summary, actions);

  const layer: LayerHandle = createLayer({
    host: options.host,
    className: 'pz-added pz-sheet',
    content: card,
    label: title.textContent,
    onOpen: () => checkout.focus?.(),
    // One sheet per add: a closed one leaves the page.
    onClose: () => window.setTimeout(() => layer.destroy(), 0),
  });
  close.addEventListener('click', () => layer.close());
  keep.addEventListener('click', () => layer.close());
  checkout.addEventListener('click', () => {
    checkout.disabled = true;
    navigate(options.cartUrl || body.cartUrl);
  });
  again.addEventListener('click', () => {
    layer.close();
    options.onAgain();
  });
  layer.open();
  return layer;
}
